import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { globalSearch, changeMyPassword } from '../../services/api';
import { SearchResults } from '../../types';
import { encodeId } from '../../utils/hashids';

const titles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/customers': 'Customers',
  '/leads':     'Leads',
  '/deals':     'Deals',
};

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super Admin', tenant_admin: 'Admin', manager: 'Manager', marketing: 'Marketing', agent: 'Agent',
};

const Topbar: React.FC = () => {
  const { user, logout } = useAuth();
  const location  = useLocation();
  const history   = useHistory();
  const title     = titles[location.pathname] ?? 'CRM';

  // ── Search ────────────────────────────────────────────────────────────────
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const debounceRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef    = useRef<HTMLDivElement>(null);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setResults(null); setSearchOpen(false); return; }
    try {
      const data = await globalSearch(q);
      setResults(data);
      setSearchOpen(true);
    } catch { setResults(null); }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doSearch]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navigate = (path: string) => { setSearchOpen(false); setQuery(''); history.push(path); };
  const hasResults = results && (results.leads.length > 0 || results.customers.length > 0 || results.deals.length > 0);

  // ── Profile dropdown ──────────────────────────────────────────────────────
  const [dropOpen, setDropOpen]     = useState(false);
  const dropRef                     = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Change password modal ─────────────────────────────────────────────────
  const [showPwd, setShowPwd]       = useState(false);
  const [curPwd, setCurPwd]         = useState('');
  const [newPwd, setNewPwd]         = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving]   = useState(false);
  const [pwdError, setPwdError]     = useState('');
  const [pwdDone, setPwdDone]       = useState(false);
  const [showCur, setShowCur]       = useState(false);
  const [showNew, setShowNew]       = useState(false);

  const openPwdModal = () => {
    setDropOpen(false);
    setCurPwd(''); setNewPwd(''); setConfirmPwd('');
    setPwdError(''); setPwdDone(false);
    setShowCur(false); setShowNew(false);
    setShowPwd(true);
  };

  const handleChangePwd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { setPwdError('New passwords do not match.'); return; }
    if (newPwd.length < 6)    { setPwdError('New password must be at least 6 characters.'); return; }
    setPwdSaving(true); setPwdError('');
    try {
      await changeMyPassword(curPwd, newPwd);
      setPwdDone(true);
    } catch (err: any) {
      setPwdError(err?.response?.data?.message ?? 'Failed to change password.');
    } finally { setPwdSaving(false); }
  };

  const handleLogout = async () => { setDropOpen(false); await logout(); };

  const initials = user?.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) ?? 'U';

  return (
    <>
      <header className="topbar">
        <div className="topbar-left">
          <button
            className="topbar-sidebar-toggle"
            onClick={() => window.dispatchEvent(new Event('sidebar-toggle'))}
            title="Toggle sidebar"
            aria-label="Toggle sidebar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
          </button>
          <span className="topbar-title">{title}</span>
        </div>

        {/* Search */}
        <div className="topbar-search" ref={searchRef}>
          <svg className="topbar-search-icon" width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            placeholder="Search…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => { if (results && query.length >= 2) setSearchOpen(true); }}
          />
          {searchOpen && (
            <div className="search-dropdown">
              {!hasResults && <div className="search-empty">No results for "{query}"</div>}
              {results && results.leads.length > 0 && (
                <>
                  <div className="search-group-label">Leads</div>
                  {results.leads.map(l => (
                    <div key={l.id} className="search-result-item" onClick={() => navigate(`/leads/${encodeId(l.id)}`)}>
                      <span className="search-result-name">{l.name}</span>
                      <span className="search-result-sub">{l.email}{l.company ? ` · ${l.company}` : ''}</span>
                    </div>
                  ))}
                </>
              )}
              {results && results.customers.length > 0 && (
                <>
                  <div className="search-group-label">Customers</div>
                  {results.customers.map(c => (
                    <div key={c.id} className="search-result-item" onClick={() => navigate('/customers')}>
                      <span className="search-result-name">{c.name}</span>
                      <span className="search-result-sub">{c.email}{c.company ? ` · ${c.company}` : ''}</span>
                    </div>
                  ))}
                </>
              )}
              {results && results.deals.length > 0 && (
                <>
                  <div className="search-group-label">Deals</div>
                  {results.deals.map(d => (
                    <div key={d.id} className="search-result-item" onClick={() => navigate(`/deals/${encodeId(d.id)}`)}>
                      <span className="search-result-name">{d.title}</span>
                      <span className="search-result-sub">{d.stage} · ₹{d.value.toLocaleString()}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Profile area */}
        <div className="topbar-right" style={{ position: 'relative' }} ref={dropRef}>
          <button
            onClick={() => setDropOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
              borderRadius: 8, transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <div className="avatar">{initials}</div>
            <div style={{ textAlign: 'left', lineHeight: 1.2 }}>
              <div className="topbar-user-name" style={{ margin: 0 }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{ROLE_LABEL[user?.role ?? ''] ?? user?.role}</div>
            </div>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {/* Dropdown */}
          {dropOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              background: '#fff', border: '1px solid var(--border)',
              borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              width: 200, zIndex: 999, overflow: 'hidden',
            }}>
              {/* User info header */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: '#f8fafc' }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{user?.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{user?.email}</div>
              </div>

              {/* Change password */}
              <button
                onClick={openPwdModal}
                style={dropItemStyle}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8f7ff')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Change Password
              </button>

              <div style={{ borderTop: '1px solid var(--border)' }} />

              {/* Sign out */}
              <button
                onClick={handleLogout}
                style={{ ...dropItemStyle, color: '#dc2626' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#fff5f5')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Change Password Modal ────────────────────────────────────────── */}
      {showPwd && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setShowPwd(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 14, width: 420, boxShadow: '0 12px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            {/* Modal header */}
            <div style={{ padding: '22px 28px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--navy)' }}>Change Password</h2>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                  Logged in as <strong>{user?.name}</strong>
                </p>
              </div>
              <button onClick={() => setShowPwd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: '20px 28px 24px' }}>
              {pwdDone ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#166534', marginBottom: 6 }}>Password changed!</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>Your new password is active immediately.</div>
                  <button className="btn-primary" onClick={() => setShowPwd(false)}>Done</button>
                </div>
              ) : (
                <form onSubmit={handleChangePwd}>
                  {/* Current password */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={fldLabel}>Current Password <span style={{ color: '#ef4444' }}>*</span></label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showCur ? 'text' : 'password'}
                        className="form-input" required
                        placeholder="Enter current password"
                        value={curPwd} onChange={e => setCurPwd(e.target.value)}
                        style={{ width: '100%', paddingRight: 36 }}
                        autoFocus
                      />
                      <button type="button" onClick={() => setShowCur(v => !v)} style={eyeBtn}>
                        {showCur ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{ borderTop: '1px solid var(--border)', margin: '18px 0 14px' }} />

                  {/* New password */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={fldLabel}>New Password <span style={{ color: '#ef4444' }}>*</span></label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showNew ? 'text' : 'password'}
                        className="form-input" required
                        placeholder="Min 6 characters"
                        value={newPwd} onChange={e => setNewPwd(e.target.value)}
                        style={{ width: '100%', paddingRight: 36 }}
                      />
                      <button type="button" onClick={() => setShowNew(v => !v)} style={eyeBtn}>
                        {showNew ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  {/* Confirm */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={fldLabel}>Confirm New Password <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="password"
                      className="form-input" required
                      placeholder="Re-enter new password"
                      value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                      style={{ width: '100%' }}
                    />
                    {/* Strength indicator */}
                    {newPwd.length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', gap: 4, alignItems: 'center' }}>
                        {[1, 2, 3, 4].map(i => (
                          <div key={i} style={{
                            height: 3, flex: 1, borderRadius: 2,
                            background: pwdStrength(newPwd) >= i
                              ? ['', '#ef4444', '#f59e0b', '#22c55e', '#16a34a'][pwdStrength(newPwd)]
                              : '#e2e8f0',
                          }} />
                        ))}
                        <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4, whiteSpace: 'nowrap' }}>
                          {['', 'Weak', 'Fair', 'Good', 'Strong'][pwdStrength(newPwd)]}
                        </span>
                      </div>
                    )}
                  </div>

                  {pwdError && (
                    <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '9px 13px', fontSize: 13, marginBottom: 16 }}>
                      {pwdError}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button type="button" className="btn-secondary" onClick={() => setShowPwd(false)}>Cancel</button>
                    <button type="submit" className="btn-primary" disabled={pwdSaving} style={{ minWidth: 150 }}>
                      {pwdSaving ? 'Updating…' : 'Change Password'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── helpers ──────────────────────────────────────────────────────────────────

function pwdStrength(pwd: string): number {
  let score = 0;
  if (pwd.length >= 6)  score++;
  if (pwd.length >= 10) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/\d/.test(pwd) && /[^A-Za-z0-9]/.test(pwd)) score++;
  return Math.min(score, 4);
}

const dropItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '10px 16px',
  background: '#fff', border: 'none', cursor: 'pointer',
  fontSize: 14, color: 'var(--text)', textAlign: 'left',
  transition: 'background 0.15s',
};

const fldLabel: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5, color: 'var(--text)',
};

const eyeBtn: React.CSSProperties = {
  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 2,
};

export default Topbar;
